import * as CaseProto from "@/protobuf/case_pb";
import { ScanDataObject } from "@/typed/CaseStateData";
import { ModelFileTypes } from "@/typed/ModelFileTypes";
import { ModelType } from "@/typed/ModelTypes";
import CaseProtoWorker from "@/worker/caseProto?worker";
import { createWorkerPool } from "@/worker/common";

interface Payload {
  version: string;
  upper?: { file_type: ModelFileTypes; file_hash: string; file: Uint8Array };
  lower?: { file_type: ModelFileTypes; file_hash: string; file: Uint8Array };
}

const protoMap: Partial<Record<ModelType, { protoModule: any; protoType: string }>> = {
  scan: { protoModule: CaseProto, protoType: "DentalCaseData" }
};

const encodeDentalCaseData = createWorkerPool<Payload, Uint8Array | undefined, Uint8Array>(
  CaseProtoWorker,
  (output) => output ?? new Uint8Array()
);

export const arrayBufferToHash = async (buffer: ArrayBuffer): Promise<string> => {
  // Read the file as an ArrayBuffer.
  let cryptoObj;

  // For dynamic use in browser (former) or node (latter)
  if (typeof window !== "undefined" && window.crypto) {
    cryptoObj = window.crypto;
  } else {
    const webcrypto = await import("crypto");
    cryptoObj = webcrypto;
  }

  // Now use cryptoObj.subtle, etc.
  const subtle = cryptoObj.subtle;

  // Compute the SHA-256 hash of the file.
  const hashBuffer = await subtle.digest("SHA-256", buffer);

  // Convert the hash ArrayBuffer into a hex string.
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");

  return hashHex;
};

export const scanObjectDataToProtobuf = async (dataType: ModelType, upperScan?: ScanDataObject, lowerScan?: ScanDataObject) => {
  // Retrieve the mapping for the given dataType.
  const mapping = protoMap[dataType];
  if (!mapping) {
    throw new Error(`Unsupported data type: ${dataType}`);
  }

  const version = "1.0.0";
  const payload: Payload = { version };

  const transfer: Transferable[] = [];

  if (upperScan) {
    const fileData = new Uint8Array(upperScan.data);
    const hash = await arrayBufferToHash(upperScan.data);
    payload.upper = { file_type: upperScan.type, file: fileData, file_hash: hash };
    transfer.push(payload.upper.file.buffer);
  }
  if (lowerScan) {
    const fileData = new Uint8Array(lowerScan.data);
    const hash = await arrayBufferToHash(lowerScan.data);
    payload.lower = { file_type: lowerScan.type, file: fileData, file_hash: hash };
    transfer.push(payload.lower.file.buffer);
  }

  return await encodeDentalCaseData({ message: payload, options: { transfer } });
};
